import { JSX } from 'react/jsx-runtime';
import i18n from 'src/lib/i18n/i18n';
import { PaymentResult, WebPaymentMethod } from '../iap.types';
import {
  CreditCard,
  Rakuten,
  PayPay,
  ApplePay,
  GooglePay,
  Paidy,
  AuPay,
  Docomo,
  SoftBank,
  Au,
  Atone,
  AtoneLite,
  PayPayBigButton,
  AuPayBigButton,
  SoftBankBigButton,
  DocomoBigButton,
  GooglePayBigButton,
  ApplePayBigButton,
  RakutenBigButton,
  PaidyBigButton,
  AuBigButton,
  AtoneBigButton,
  AtoneLiteBigButton,
  CreditCardBigButton,
  Merpay,
  MerpayBigButton,
} from './Icons';
import { KomojuHandler } from './handlers/KomojuHandler';
import { ProductId } from 'src/replicant/iap';
import { StripeHandler } from './handlers/StripeHandler';
import { AtoneHandler } from './handlers/AtoneHandler';
import { isProductIdGems } from 'src/replicant/iap/iap.util';
import { gemsRewardTable } from 'src/replicant/iap/iap.ruleset';

/**
 * Map of payment methods to icons (generic).
 */
export const paymentMethodsIcons: Record<WebPaymentMethod, JSX.Element> = {
  komoju_credit_card: <CreditCard />,
  komoju_rakutenpay: <Rakuten />,
  komoju_paypay: <PayPay />,
  stripe_applePay: <ApplePay />,
  stripe_googlePay: <GooglePay />,
  komoju_paidy: <Paidy />,
  komoju_aupay: <AuPay />,
  komoju_docomo: <Docomo />,
  komoju_softbank: <SoftBank />,
  komoju_au: <Au />,
  komoju_merpay: <Merpay />,
  atone_bnpl: <Atone />,
  atone_lite: <AtoneLite />,
};

/**
 * Map of payment methods to icons (payment buttons).
 */
export const paymentMethodsButtonIcons: Record<WebPaymentMethod, JSX.Element> =
  {
    komoju_paypay: <PayPayBigButton />,
    komoju_aupay: <AuPayBigButton />,
    komoju_docomo: <DocomoBigButton />,
    komoju_softbank: <SoftBankBigButton />,
    komoju_credit_card: <CreditCardBigButton />,
    stripe_googlePay: <GooglePayBigButton />,
    stripe_applePay: <ApplePayBigButton />,
    komoju_rakutenpay: <RakutenBigButton />,
    komoju_paidy: <PaidyBigButton />,
    komoju_au: <AuBigButton />,
    komoju_merpay: <MerpayBigButton />,
    atone_bnpl: <AtoneBigButton />,
    atone_lite: <AtoneLiteBigButton />,
  };

/**
 * Map of payment methods to names.
 */
export const paymentMethodsNames: Record<WebPaymentMethod, () => string> = {
  komoju_credit_card: () => i18n('komojuHostedFields.creditCard'),
  komoju_paidy: () => i18n('komojuHostedFields.paidy'),
  komoju_rakutenpay: () => i18n('komojuHostedFields.rakutenpay'),
  komoju_paypay: () => 'PayPay',
  komoju_merpay: () => i18n('komojuHostedFields.merpay'),
  komoju_au: () => 'au',
  komoju_aupay: () => 'au PAY',
  komoju_docomo: () => 'Docomo',
  komoju_softbank: () => 'Softbank',
  stripe_googlePay: () => 'Google Pay',
  stripe_applePay: () => 'Apple Pay',
  atone_lite: () => i18n('openWebPay.atone.oneTime'),
  atone_bnpl: () => i18n('openWebPay.atone.nextMonth'),
};

/**
 * Map of payment methods to their handlers.
 */
export const paymentMethodHandlers: Record<
  WebPaymentMethod,
  (opts: {
    productId: ProductId;
    close: (result: PaymentResult) => void;
    onPurchaseStart: () => void;
    onAgeConfirm: (accepted: boolean) => void;
  }) => JSX.Element
> = {
  komoju_credit_card: (opts) => (
    <KomojuHandler paymentMethod="credit_card" {...opts} />
  ),
  komoju_rakutenpay: (opts) => (
    <KomojuHandler paymentMethod="rakutenpay" {...opts} />
  ),
  komoju_paidy: (opts) => <KomojuHandler paymentMethod="paidy" {...opts} />,
  komoju_aupay: (opts) => <KomojuHandler paymentMethod="aupay" {...opts} />,
  komoju_docomo: (opts) => <KomojuHandler paymentMethod="docomo" {...opts} />,
  komoju_softbank: (opts) => (
    <KomojuHandler paymentMethod="softbank" {...opts} />
  ),
  komoju_au: (opts) => <KomojuHandler paymentMethod="au" {...opts} />,
  stripe_applePay: (opts) => (
    <StripeHandler paymentMethod="stripe_applePay" {...opts} />
  ),
  stripe_googlePay: (opts) => (
    <StripeHandler paymentMethod="stripe_googlePay" {...opts} />
  ),
  atone_bnpl: (opts) => <AtoneHandler paymentMethod="atone_bnpl" {...opts} />,
  atone_lite: (opts) => <AtoneHandler paymentMethod="atone_lite" {...opts} />,
  komoju_paypay: (opts) => <KomojuHandler paymentMethod="paypay" {...opts} />,
  komoju_merpay: (opts) => <KomojuHandler paymentMethod="merpay" {...opts} />,
};

/**
 * Product names.
 */
export const productNames: Record<
  ProductId,
  ({ gems }: { gems: number }) => string
> = {
  buruburu_gems_100: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),
  buruburu_gems_1000: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),
  buruburu_gems_12000: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),
  buruburu_gems_18000: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),
  buruburu_gems_3200: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),
  buruburu_gems_350: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),
  buruburu_gems_6000: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),

  buruburu_gems_200: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),
  buruburu_gems_1000_1200: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),
  buruburu_gems_2000: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),
  buruburu_gems_5500: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),
  buruburu_gems_10000: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),
  buruburu_gems_20000: ({ gems }) =>
    i18n('openWebPayment.products.gem', { numGems: gems }),

  buruburu_pet_starter_pack: () =>
    i18n('match2.shopBundles.buruburu_pet_starter_pack'),
  buruburu_pet_starter_pack_2: () =>
    i18n('match2.shopBundles.buruburu_pet_starter_pack_2'),
  buruburu_pet_starter_pack_3: () =>
    i18n('match2.shopBundles.buruburu_pet_starter_pack_3'),
  buruburu_starter_pack: () => i18n('match2.shopBundles.buruburu_starter_pack'),
  buruburu_mini_pack: () => i18n('match2.shopBundles.buruburu_mini_pack'),
  buruburu_small_pack: () => i18n('match2.shopBundles.buruburu_small_pack'),
  buruburu_medium_pack: () => i18n('match2.shopBundles.buruburu_medium_pack'),
  buruburu_large_pack: () => i18n('match2.shopBundles.buruburu_large_pack'),
  buruburu_grande_pack: () => i18n('match2.shopBundles.buruburu_grande_pack'),
  buruburu_jumbo_pack: () => i18n('match2.shopBundles.buruburu_jumbo_pack'),

  buruburu_sale_rm_04p_2: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_rm_10p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_rm_16p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_rm_22p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_rm_28p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_rm_34p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_rm_40p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_rm_46p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_cm_04p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_cm_10p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_cm_16p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_cm_22p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_cm_28p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_cm_34p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_cm_40p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_cm_46p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_cm_52p: () => i18n('openWebPayment.products.endless_sale'),
  buruburu_sale_cm_58p: () => i18n('openWebPayment.products.endless_sale'),

  buruburu_booster_sale: () => i18n('openWebPayment.products.booster_sale'),

  buruburu_buru_pass: () => i18n('match2.battlePass.title'),

  // Not active in game.
  buruburu_flashsale_continue: () => 'Flash Sale',
  buruburu_conversionsale_foryou: () => 'Sale For You',
  buruburu_piggy_bank_200: () => 'Piggy Bank',
  buruburu_piggy_bank_500: () => 'Piggy Bank',
};

/**
 * Get the product i18n name
 */
export function getProductName(productId: ProductId) {
  if (isProductIdGems(productId)) {
    const rewards = gemsRewardTable[productId];
    const gems = (rewards.freeGems ?? 0) + (rewards.paidGems ?? 0);

    return productNames[productId]({ gems });
  }

  // All our products have gems, but just in case we have a new product without gems
  return productNames[productId as ProductId]({ gems: 0 });
}
